import { ExternalLink, ShieldCheck } from 'lucide-react';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { toTitleCase } from '@/react/common/string-utils';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';
import { Badge } from '@@/Badge';
import { Alert } from '@@/Alert';
import { Icon } from '@@/Icon';

import { RescanWorkloadButton } from './RescanWorkloadButton';
import { useVulnerabilityReports } from './queries/useVulnerabilityReports';
import { severityBadgeType } from './severityDisplay';
import {
  SEVERITY_SUMMARY_FIELD,
  TRIVY_SEVERITIES,
  Vulnerability,
  VulnerabilityReport,
  severityRank,
} from './types';

/**
 * Shows every VulnerabilityReport for one workload. A workload with several
 * containers has one report each, so more than one section can render.
 */
export function VulnerabilityDetailsModal({
  environmentId,
  namespace,
  resourceName,
  resourceKind,
  onDismiss,
}: {
  environmentId: EnvironmentId;
  namespace?: string;
  resourceName?: string;
  resourceKind?: string;
  onDismiss: () => void;
}) {
  const reportsQuery = useVulnerabilityReports({
    environmentId,
    namespace,
    resourceName,
    resourceKind,
    enabled: Number.isFinite(environmentId) && !!namespace && !!resourceName,
  });

  return (
    <Modal onDismiss={onDismiss} size="lg" aria-label="Vulnerability details">
      <Modal.Header title={`Vulnerabilities in ${namespace}/${resourceName}`} />

      <Modal.Body>
        {reportsQuery.isLoading && (
          <div className="text-muted">Loading vulnerability reports…</div>
        )}

        {reportsQuery.isError && (
          <Alert color="error" title="Unable to load vulnerability reports">
            The scanner reports could not be read from this environment. A
            non-admin user needs read access to the aquasecurity.github.io
            resources.
          </Alert>
        )}

        {reportsQuery.isSuccess && reportsQuery.data.length === 0 && (
          <Alert color="info" title="No scanner reports found">
            The alert fired on scraped metrics, but no VulnerabilityReport
            exists for this workload. It may have been rescanned or removed.
          </Alert>
        )}

        {reportsQuery.data?.map((report) => (
          <ReportSection key={report.metadata.name} report={report} />
        ))}
      </Modal.Body>

      <Modal.Footer>
        {!!namespace && !!resourceName && (
          <RescanWorkloadButton
            environmentId={environmentId}
            namespace={namespace}
            workload={resourceName}
            onRescanned={onDismiss}
          />
        )}
        <Button
          type="button"
          color="default"
          onClick={onDismiss}
          data-cy="vulnerability-details-close"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function ReportSection({ report }: { report: VulnerabilityReport }) {
  const { artifact, summary, scanner, updateTimestamp } = report.report;
  const vulnerabilities = sortBySeverityThenScore(
    report.report.vulnerabilities ?? []
  );
  const container = report.metadata.labels?.['trivy-operator.container.name'];
  const fixable = vulnerabilities.filter((v) => !!v.fixedVersion).length;

  return (
    <div className="mb-6">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">
          {artifact.repository}:{artifact.tag ?? 'latest'}
        </span>
        {container && <Badge type="muted">container: {container}</Badge>}
        {scanner && (
          <span className="text-muted text-xs">
            {scanner.name} {scanner.version}
          </span>
        )}
        {updateTimestamp && (
          <span className="text-muted text-xs">scanned {updateTimestamp}</span>
        )}
      </div>

      {artifact.digest && (
        <div className="text-muted mb-3 font-mono text-xs break-all">
          {artifact.digest}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {TRIVY_SEVERITIES.map((severity) => (
          <Badge key={severity} type={severityBadgeType(severity)}>
            {toTitleCase(severity)}{' '}
            {summary[SEVERITY_SUMMARY_FIELD[severity]] ?? 0}
          </Badge>
        ))}
      </div>

      <div className="mb-3 flex items-center gap-2 text-sm">
        <Icon icon={ShieldCheck} />
        <span>
          {fixable} of {vulnerabilities.length} have a fix available
        </span>
      </div>

      <div className="max-h-96 overflow-auto">
        <table className="table-sm table">
          <thead>
            <tr>
              <th>Severity</th>
              <th>CVE</th>
              <th>Score</th>
              <th>Package</th>
              <th>Installed</th>
              <th>Fixed in</th>
            </tr>
          </thead>
          <tbody>
            {vulnerabilities.map((vulnerability) => (
              <VulnerabilityRow
                key={`${vulnerability.vulnerabilityID}-${vulnerability.resource}-${vulnerability.installedVersion}`}
                vulnerability={vulnerability}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VulnerabilityRow({ vulnerability }: { vulnerability: Vulnerability }) {
  return (
    <tr>
      <td>
        <Badge type={severityBadgeType(vulnerability.severity)}>
          {toTitleCase(vulnerability.severity)}
        </Badge>
      </td>
      <td>
        {vulnerability.primaryLink ? (
          <a
            href={vulnerability.primaryLink}
            target="_blank"
            rel="noreferrer"
            title={vulnerability.title}
            className="inline-flex items-center gap-1"
          >
            {vulnerability.vulnerabilityID}
            <Icon icon={ExternalLink} />
          </a>
        ) : (
          vulnerability.vulnerabilityID
        )}
      </td>
      <td>{vulnerability.score ?? '—'}</td>
      <td className="break-all">{vulnerability.resource}</td>
      <td className="break-all">{vulnerability.installedVersion}</td>
      <td className="break-all">
        {vulnerability.fixedVersion || (
          <span className="text-muted">no fix</span>
        )}
      </td>
    </tr>
  );
}

function sortBySeverityThenScore(
  vulnerabilities: Array<Vulnerability>
): Array<Vulnerability> {
  return [...vulnerabilities].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity);
    if (bySeverity !== 0) {
      return bySeverity;
    }
    return (b.score ?? 0) - (a.score ?? 0);
  });
}
